// Niveles de fidelización (Bronce/Plata/Oro…). POS → Configuración → Niveles.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, NivelFid, Switch, fieldCls, money, pos, posRpc } from './shared';

const vacio = { codigo: '', nombre: '', monto_min: 0, multiplicador_puntos: 1, descuento_pct: 0, orden: 0, es_activo: true };

export function Niveles() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<NivelFid[]>({ queryKey: ['pos-niveles'], queryFn: () => pos.list<NivelFid>('fidelidad_niveles', '?select=*&order=monto_min') });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (n?: NivelFid) => {
    if (n) { setEditId(n.id); setForm({ codigo: n.codigo ?? '', nombre: n.nombre, monto_min: n.monto_min, multiplicador_puntos: n.multiplicador_puntos, descuento_pct: n.descuento_pct, orden: n.orden, es_activo: n.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, codigo: form.codigo || null, monto_min: Number(form.monto_min) || 0, multiplicador_puntos: Number(form.multiplicador_puntos) || 1, descuento_pct: Number(form.descuento_pct) || 0, orden: Number(form.orden) || 0 };
      return editId ? pos.update('fidelidad_niveles', editId, body) : pos.create('fidelidad_niveles', body);
    },
    onSuccess: () => { toast.success('Nivel guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-niveles'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => pos.remove('fidelidad_niveles', id),
    onSuccess: () => { toast.success('Nivel eliminado'); void qc.invalidateQueries({ queryKey: ['pos-niveles'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const recalcular = useMutation({
    mutationFn: () => posRpc<{ clientes: number }>('fidelidad_recalcular_niveles', {}),
    onSuccess: (r) => toast.success(`${r.clientes} cliente(s) reclasificado(s)`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Award className="w-5 h-5" /> Niveles de fidelización</h1>
        <div className="flex gap-2">
          <Button variant="outline" isLoading={recalcular.isPending} onClick={async () => { if (await dlg.confirmar('¿Recalcular el nivel de todos los clientes?', { detalle: 'Usa las compras de los últimos 12 meses de cada cliente.' })) recalcular.mutate(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Recalcular niveles
          </Button>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo nivel</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">El nivel se asigna por la compra de los últimos 12 meses (umbral). Beneficios: multiplica los puntos ganados y fija el % de descuento del cliente. El proceso nocturno lo recalcula automáticamente.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Niveles ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nivel</th><th className="py-2 pr-3 text-right">Compra mínima 12m</th><th className="py-2 pr-3 text-right">x Puntos</th><th className="py-2 pr-3 text-right">% Desc.</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin niveles</td></tr>}
              {rows.map((n) => (
                <tr key={n.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{n.nombre}</td>
                  <td className="py-2 pr-3 text-right">{money(n.monto_min)}</td>
                  <td className="py-2 pr-3 text-right">{Number(n.multiplicador_puntos)}</td>
                  <td className="py-2 pr-3 text-right">{Number(n.descuento_pct)}%</td>
                  <td className="py-2 pr-3">{n.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(n)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${n.nombre}"?`, { peligro: true })) eliminar.mutate(n.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar nivel' : 'Nuevo nivel'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => set({ orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Compra mínima (últimos 12 meses)"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto_min} onChange={(e) => set({ monto_min: Number(e.target.value) })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Multiplicador de puntos"><input type="number" step="0.001" min="0" className={fieldCls} value={form.multiplicador_puntos} onChange={(e) => set({ multiplicador_puntos: Number(e.target.value) })} /></Campo>
              <Campo label="% de descuento"><input type="number" step="0.01" min="0" className={fieldCls} value={form.descuento_pct} onChange={(e) => set({ descuento_pct: Number(e.target.value) })} /></Campo>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
